import { useTranslations } from '@/hooks/useTranslations';
import type { GanttLink, GanttTask } from '@/lib/gantt/types';
import { getGanttInstanceFactory, getGanttSingleton, type DhtmlxGanttInstance } from '@/lib/projects/dhtmlxGanttFactory';
import { cn } from '@/lib/utils';
import { usePage } from '@inertiajs/react';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import { useLayoutEffect, useRef } from 'react';

interface MppGanttViewerProps {
    tasks: GanttTask[];
    links: GanttLink[];
    className?: string;
}

const ROW_HEIGHT = 38;
const TODAY_MARKER_ID = 'mpp-gantt-today';

function escapeHtml(value: string): string {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
}

/**
 * Read-only DHTMLX Gantt for a parsed project file. Modeled on the read-only
 * PortfolioProjectGanttCard, but renders dependency links, milestones and progress,
 * and carries none of the CRM permission/write-back machinery.
 */
const VIEWER_STYLE = `
  .mpp-gantt-chart {
    width: 100%;
    height: 100%;
  }
  .mpp-gantt-chart .gantt_container {
    border: none !important;
    font-family: var(--font-sans) !important;
    background: var(--card) !important;
    color: var(--card-foreground) !important;
  }
  .mpp-gantt-chart .gantt_grid_scale,
  .mpp-gantt-chart .gantt_task_scale {
    background: color-mix(in oklch, var(--muted) 32%, transparent) !important;
    color: var(--muted-foreground) !important;
    border-color: var(--border) !important;
  }
  .mpp-gantt-chart .gantt_grid_head_cell,
  .mpp-gantt-chart .gantt_scale_cell {
    color: var(--muted-foreground) !important;
    font-weight: 500 !important;
    border-color: color-mix(in oklch, var(--border) 45%, transparent) !important;
    text-transform: none !important;
  }
  .mpp-gantt-chart .gantt_row,
  .mpp-gantt-chart .gantt_task_row {
    border-bottom: 1px solid color-mix(in oklch, var(--border) 55%, transparent) !important;
    background: var(--card) !important;
  }
  .mpp-gantt-chart .gantt_row:hover,
  .mpp-gantt-chart .gantt_task_row:hover {
    background: color-mix(in oklch, var(--muted) 20%, transparent) !important;
  }
  .mpp-gantt-chart .gantt_cell,
  .mpp-gantt-chart .gantt_grid_data .gantt_cell {
    color: var(--foreground) !important;
    border-right: 1px solid color-mix(in oklch, var(--border) 40%, transparent) !important;
    font-size: 12px !important;
  }
  .mpp-gantt-chart .gantt_task_line {
    border-radius: 4px !important;
    background: var(--primary) !important;
    border: 1px solid color-mix(in oklch, var(--primary) 80%, black 20%) !important;
  }
  .mpp-gantt-chart .gantt_task_line.gantt_project {
    background: color-mix(in oklch, var(--primary) 55%, var(--card)) !important;
    border-color: color-mix(in oklch, var(--primary) 65%, transparent) !important;
  }
  .mpp-gantt-chart .gantt_task_progress {
    background: color-mix(in oklch, var(--primary) 55%, black 30%) !important;
    border-radius: 4px 0 0 4px !important;
  }
  .mpp-gantt-chart .gantt_task_content {
    color: var(--primary-foreground) !important;
    font-size: 12px !important;
  }
  .mpp-gantt-chart .gantt_task_line.gantt_milestone {
    background: var(--warning, var(--primary)) !important;
    border-color: color-mix(in oklch, var(--warning, var(--primary)) 80%, black 20%) !important;
  }
  .mpp-gantt-chart .gantt_grid_head_add,
  .mpp-gantt-chart .gantt_add {
    display: none !important;
  }
  .mpp-gantt-chart .gantt_marker.mpp_today {
    background-color: #dc2626 !important;
    width: 1px !important;
  }
  .dark .mpp-gantt-chart .gantt_marker.mpp_today {
    background-color: #f87171 !important;
  }
`;

export default function MppGanttViewer({ tasks, links, className }: MppGanttViewerProps) {
    const { t } = useTranslations();
    const containerRef = useRef<HTMLDivElement>(null);
    const page = usePage<{ locale?: string }>();
    const locale = page.props.locale ?? 'en';

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // Prefer a fresh, isolated instance (safe to fully destroy on unmount). Only
        // fall back to the shared singleton if the runtime can't mint one — and in that
        // case NEVER call destructor() in cleanup, since that tears down the datastores
        // the next mount (or another gantt on the app) relies on ("tasksStore" crash).
        const factory = getGanttInstanceFactory();
        const isolated = Boolean(factory);
        const instance: DhtmlxGanttInstance = factory ? factory() : getGanttSingleton();

        instance.config.readonly = true;
        instance.config.drag_links = false;
        instance.config.drag_move = false;
        instance.config.drag_resize = false;
        instance.config.drag_progress = false;
        instance.config.open_tree_initially = true;
        instance.config.row_height = ROW_HEIGHT;
        instance.config.date_format = '%Y-%m-%d';
        instance.config.scale_height = 50;
        instance.config.min_column_width = 28;
        instance.config.scales = [
            { unit: 'month', step: 1, format: '%Y-%m' },
            { unit: 'day', step: 1, format: '%d' },
        ];

        const formatDate = instance.date.date_to_str('%Y-%m-%d');

        instance.config.columns = [
            {
                name: 'text',
                label: t('mpp.show.gantt.task', 'Task'),
                tree: true,
                width: 280,
                resize: true,
                template: (task: GanttTask): string => escapeHtml(task.text),
            },
            {
                name: 'start_date',
                label: t('mpp.show.gantt.start', 'Start'),
                align: 'center',
                width: 96,
                resize: true,
                template: (task: { start_date?: Date }): string => (task.start_date ? formatDate(task.start_date) : ''),
            },
            {
                name: 'finish',
                label: t('mpp.show.gantt.finish', 'Finish'),
                align: 'center',
                width: 96,
                resize: true,
                template: (task: GanttTask): string => escapeHtml(task.finish ?? ''),
            },
            {
                name: 'duration',
                label: t('mpp.show.gantt.duration', 'Duration'),
                align: 'center',
                width: 80,
                resize: true,
                template: (task: GanttTask): string => `${task.duration} ${t('mpp.show.gantt.days', 'd')}`,
            },
            {
                name: 'progress',
                label: t('mpp.show.gantt.progress', '% Complete'),
                align: 'center',
                width: 90,
                resize: true,
                template: (task: GanttTask): string => `${Math.round((task.progress ?? 0) * 100)}%`,
            },
            {
                name: 'resources',
                label: t('mpp.show.gantt.resources', 'Resources'),
                align: 'left',
                width: 160,
                resize: true,
                template: (task: GanttTask): string => escapeHtml(task.resources ?? ''),
            },
        ];

        try {
            instance.plugins({ marker: true });
            instance.init(container);
            instance.clearAll();
            instance.parse({ data: tasks, links });

            if (typeof instance.getMarker === 'function' && instance.getMarker(TODAY_MARKER_ID)) {
                instance.deleteMarker(TODAY_MARKER_ID);
            }
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            instance.addMarker({
                id: TODAY_MARKER_ID,
                start_date: today,
                css: 'mpp_today',
                title: t('mpp.show.gantt.today', 'Today'),
                text: '',
            });
            if (typeof instance.renderMarkers === 'function') {
                instance.renderMarkers();
            }
            instance.showDate(today);
        } catch (error) {
            console.error('Failed to render MPP Gantt chart', error);
        }

        return () => {
            try {
                if (typeof instance.getMarker === 'function' && instance.getMarker(TODAY_MARKER_ID)) {
                    instance.deleteMarker(TODAY_MARKER_ID);
                }
                instance.clearAll();
                // Only fully destroy a fresh, isolated instance. Destroying the shared
                // singleton would break the next chart that reuses it.
                if (isolated && typeof instance.destructor === 'function') {
                    instance.destructor();
                }
            } catch {
                // Ignore teardown races on quick unmount.
            }
        };
    }, [tasks, links, locale, t]);

    return (
        <>
            <style>{VIEWER_STYLE}</style>
            <div ref={containerRef} className={cn('mpp-gantt-chart', className)} />
        </>
    );
}
